<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Button from 'next/button/Button.vue';
import SectionLayout from './SectionLayout.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();
const useAlertMessage = useAlert();

const linkValidDays = ref(14);
const provider = ref('internal');
const attachInEmail = ref(false);
const saving = ref(false);

onMounted(() => {
  const settings = currentAccount.value?.settings || {};
  linkValidDays.value = Number(settings.document_signing_link_valid_days) || 14;
  provider.value = settings.document_signing_provider || 'internal';
  attachInEmail.value = Boolean(settings.document_signing_attach_in_email);
});

const handleSave = async () => {
  saving.value = true;
  try {
    await updateAccount({
      document_signing_link_valid_days: Number(linkValidDays.value) || 14,
      document_signing_provider: provider.value || 'internal',
      document_signing_attach_in_email: attachInEmail.value,
    });
    useAlertMessage(t('GENERAL_SETTINGS.DOCUMENT_SIGNING.SAVE_SUCCESS'));
  } catch {
    useAlertMessage(t('GENERAL_SETTINGS.DOCUMENT_SIGNING.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.DOCUMENT_SIGNING.TITLE')"
    :description="t('GENERAL_SETTINGS.DOCUMENT_SIGNING.DESCRIPTION')"
  >
    <div class="flex flex-col gap-4">
      <div class="w-48">
        <label
          class="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          {{ t('GENERAL_SETTINGS.DOCUMENT_SIGNING.LINK_VALID_DAYS') }}
        </label>
        <input
          v-model="linkValidDays"
          type="number"
          min="1"
          max="90"
          class="reset-base w-full border border-slate-200 rounded-lg bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
        />
      </div>

      <div class="w-48">
        <label
          class="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          {{ t('GENERAL_SETTINGS.DOCUMENT_SIGNING.PROVIDER') }}
        </label>
        <select
          v-model="provider"
          class="w-full border border-slate-200 rounded-lg bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
        >
          <option value="internal">
            {{ t('GENERAL_SETTINGS.DOCUMENT_SIGNING.PROVIDER_INTERNAL') }}
          </option>
          <option value="docusign" disabled>
            {{ t('GENERAL_SETTINGS.DOCUMENT_SIGNING.PROVIDER_DOCUSIGN') }}
          </option>
        </select>
      </div>

      <label
        class="flex cursor-pointer items-start gap-3 text-sm text-slate-700 dark:text-slate-300"
      >
        <input
          v-model="attachInEmail"
          type="checkbox"
          class="mt-0.5 h-4 w-4 rounded border-slate-300 text-woot-500 focus:ring-woot-500"
        />
        <span>
          <span class="block font-medium">
            {{ t('GENERAL_SETTINGS.DOCUMENT_SIGNING.ATTACH_IN_EMAIL') }}
          </span>
          <span class="mt-0.5 block text-slate-500 dark:text-slate-400">
            {{ t('GENERAL_SETTINGS.DOCUMENT_SIGNING.ATTACH_IN_EMAIL_HELP') }}
          </span>
        </span>
      </label>

      <div>
        <Button blue :is-loading="saving" @click="handleSave">
          {{ t('GENERAL_SETTINGS.SUBMIT') }}
        </Button>
      </div>
    </div>
  </SectionLayout>
</template>
